import React, { useContext, useMemo, useState } from 'react';
import CurrentUserContext from 'contexts/CurrentUserContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import Inputs from 'components/Inputs';
import getIdentifier from 'getIdentifier';
import getTimeStamp from 'getTimeStamp';
import FormInput from 'kingpin/forms/FormInput';
import useSaveWidget from '../../../hooks/useSaveWidget';
import WidgetFooter from '../../WidgetFooter';
import { useBonusConfigOptions } from '../BonusTotalRewardWizard';
import { WidgetWrapper } from '../MetricSlideoutWizard/styles';
import ShadowedList from '../../../../ShadowedList';
import FactTableFieldsInput from '../FactTableFieldsInput';
import HeaderTotalFieldInput from '../HeaderTotalFieldInput';
import RenderModeInput from '../RenderModeInput';

export const useRewardNameOptions = ({
  bonusConfigId,
  editingSection,
}: {
  bonusConfigId: string | undefined;
  editingSection?: PerformanceBoardTypes.SlideOut.BonusRewardSection;
}) => {
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const [rewardName, setRewardName] = useState<string | undefined>(
    editingSection ? editingSection.rewardName : undefined,
  );

  const bonusProgram = useMemo(() => {
    return bonusPrograms
      .filter((bp) => !!bp.id)
      .find((bp) => bp.id === bonusConfigId);
  }, [bonusConfigId, bonusPrograms]);

  const rewardNames = useMemo(() => {
    if (!bonusProgram) {
      return [];
    }

    return bonusProgram.rewardFields.map((rewardField) => {
      return rewardField.name;
    });
  }, [bonusProgram]);

  const rewardNameOptions = useMemo((): DropdownOption[] => {
    return rewardNames.map((name) => ({
      label: name,
      key: name,
      isSelected: rewardName === name,
      onSelected: () => setRewardName(name),
    }));
  }, [rewardName, rewardNames]);

  return {
    rewardName,
    rewardNameOptions,
  };
};

const BonusRewardWizard = ({
  close,
  editingSection,
  lockedBonusConfigId,
}: {
  close: () => void;
  editingSection?: PerformanceBoardTypes.SlideOut.BonusRewardSection;
  lockedBonusConfigId?: string;
}) => {
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { bonusConfigId, bonusConfigOptions } = useBonusConfigOptions({
    editingSection,
    lockedBonusConfigId,
  });
  const { rewardName, rewardNameOptions } = useRewardNameOptions({
    editingSection,
    bonusConfigId,
  });
  const [factTableFields, setFactTableFields] = useState<string[]>(
    editingSection?.factTableFields ?? [],
  );
  const [totalValueField, setTotalValueField] = useState<string | undefined>(
    editingSection?.totalValueField,
  );
  const [renderMode, setRenderMode] = useState<
    'auto' | 'table' | 'cards' | undefined
  >(editingSection?.renderMode);

  const draftSection = useMemo(():
    | PerformanceBoardTypes.SlideOut.BonusRewardSection
    | undefined => {
    if (!bonusConfigId || !rewardName) {
      return undefined;
    }
    return {
      id: editingSection ? editingSection.id : getIdentifier(),
      title: rewardName,
      sectionType: 'Bonus Reward',
      bonusConfigId,
      rewardName,
      factTableFields,
      totalValueField,
      renderMode,
      createdOn: editingSection ? editingSection.createdOn : getTimeStamp(),
      createdBy: editingSection ? editingSection.createdBy : currentUserId,
      updatedBy: currentUserId,
      updatedOn: getTimeStamp(),
    };
  }, [
    bonusConfigId,
    currentUserId,
    editingSection,
    rewardName,
    factTableFields,
    totalValueField,
    renderMode,
  ]);

  const saveWidget = useSaveWidget({
    section: draftSection,
    editingSection,
  });

  return (
    <>
      <ShadowedList>
        <WidgetWrapper>
          <FormInput label={'Bonus Program'}>
            <Inputs.Dropdown options={bonusConfigOptions} />
          </FormInput>
          <FormInput label={'Reward Name'}>
            <Inputs.Dropdown options={rewardNameOptions} />
          </FormInput>
          <FactTableFieldsInput
            bonusConfigId={bonusConfigId}
            fields={factTableFields}
            setFields={setFactTableFields}
          />
          <HeaderTotalFieldInput
            bonusConfigId={bonusConfigId}
            rewardName={rewardName}
            value={totalValueField}
            setValue={setTotalValueField}
          />
          <RenderModeInput value={renderMode} setValue={setRenderMode} />
        </WidgetWrapper>
      </ShadowedList>

      <WidgetFooter
        saveWidget={saveWidget}
        sectionType="Bonus Total Reward"
        isDisabled={draftSection === undefined}
        close={close}
      />
    </>
  );
};

export default BonusRewardWizard;
